import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, Eye, Edit, Calendar, MapPin, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RelocationsList = () => {
  const navigate = useNavigate();
  const [relocations, setRelocations] = useState([]);
  const [clients, setClients] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const password = sessionStorage.getItem('admin_password');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchRelocations();
    fetchClients();
  }, []);

  const fetchRelocations = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/relocations`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      const data = await response.json();
      setRelocations(data);
    } catch (error) {
      logger.error('Error fetching relocations:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/clients`, {
        headers: { 'Authorization': `Basic ${btoa(':' + password)}` }
      });
      const data = await response.json();
      const clientsMap = {};
      data.forEach(client => {
        clientsMap[client.id] = client;
      });
      setClients(clientsMap);
    } catch (error) {
      logger.error('Error fetching clients:', error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'quote_sent': 'bg-blue-100 text-blue-800',
      'booking_confirmed': 'bg-purple-100 text-purple-800',
      'documents_required': 'bg-yellow-100 text-yellow-800',
      'documents_submitted': 'bg-orange-100 text-orange-800',
      'documents_under_review': 'bg-orange-100 text-orange-800',
      'documents_approved': 'bg-green-100 text-green-800',
      'travel_arrangements': 'bg-indigo-100 text-indigo-800',
      'flight_booked': 'bg-indigo-100 text-indigo-800',
      'pre_travel_prep': 'bg-teal-100 text-teal-800',
      'in_transit': 'bg-pink-100 text-pink-800',
      'delivered': 'bg-green-100 text-green-800',
      'complete': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredRelocations = relocations.filter(relocation => {
    const client = clients[relocation.client_id];
    const clientName = client ? `${client.first_name} ${client.last_name}` : '';
    const searchMatch = 
      relocation.pet_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      relocation.destination.toLowerCase().includes(searchTerm.toLowerCase());
    
    const statusMatch = filterStatus === 'all' || relocation.current_status === filterStatus;
    
    return searchMatch && statusMatch;
  });

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'quote_sent', label: 'Quote Sent' },
    { value: 'booking_confirmed', label: 'Booking Confirmed' },
    { value: 'documents_required', label: 'Documents Required' },
    { value: 'documents_submitted', label: 'Documents Submitted' },
    { value: 'documents_approved', label: 'Documents Approved' },
    { value: 'flight_booked', label: 'Flight Booked' },
    { value: 'in_transit', label: 'In Transit' },
    { value: 'delivered', label: 'Delivered' },
    { value: 'complete', label: 'Complete' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading relocations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Pet Relocations</h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage and track all pet relocation journeys
              </p>
            </div>
            <Button onClick={() => navigate('/admin/relocations/new')} className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              New Relocation
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by pet name, client, or destination..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent appearance-none bg-white"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Total Relocations</div>
            <div className="text-2xl font-bold text-gray-900">{relocations.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">In Transit</div>
            <div className="text-2xl font-bold text-pink-600">
              {relocations.filter(r => r.current_status === 'in_transit').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Flight Booked</div>
            <div className="text-2xl font-bold text-indigo-600">
              {relocations.filter(r => r.current_status === 'flight_booked').length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4">
            <div className="text-sm text-gray-600">Completed</div>
            <div className="text-2xl font-bold text-green-600">
              {relocations.filter(r => r.current_status === 'complete').length}
            </div>
          </div>
        </div>

        {/* Relocations Table */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {filteredRelocations.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No relocations found</p>
              <Button 
                onClick={() => navigate('/admin/relocations/new')}
                className="mt-4"
              >
                Create First Relocation
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pet & Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Route
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Travel Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRelocations.map((relocation) => {
                    const client = clients[relocation.client_id];
                    return (
                      <tr key={relocation.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {relocation.pet_name}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {client ? `${client.first_name} ${client.last_name}` : 'Unknown Client'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-sm text-gray-900">
                            <MapPin className="w-4 h-4 text-gray-400" />
                            {relocation.origin} → {relocation.destination}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {formatDate(relocation.travel_date)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(relocation.current_status)}`}>
                            {formatStatus(relocation.current_status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/relocations/${relocation.id}`)}
                              className="flex items-center gap-1"
                              data-testid={`view-relocation-${relocation.id}`}
                            >
                              <Eye className="w-4 h-4" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/admin/relocations/${relocation.id}/edit`)}
                              className="flex items-center gap-1 text-primary border-primary hover:bg-primary/5"
                              data-testid={`edit-relocation-${relocation.id}`}
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Back to Dashboard */}
        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/dashboard')}
          >
            ← Back to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
};
